import { Typography } from '@mui/material';
import { IcaroDetailCrumbs } from 'civil-status/constants/breadcrumb';
import { Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs, { BreadcrumbItem } from 'shell/components/shared/breadcrumb';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import {
  DataDeclarationIcaro,
  FormDeclarationIcaroVM,
} from 'civil-status/view-models/form-declaration-icaro';
import { transformerDeclarationIcaro } from 'civil-status/utils/transformer-declaration-icaro';
import { Formik, Form } from 'formik';
import IcaroAccordions from 'civil-status/components/icaro-detail';
import { useEventCallback } from '@mui/material';
import {
  validationSchemaIcaro,
  validationSchemaIcaroEmpty,
} from 'civil-status/constants/schema-icaro';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { AxiosResponse } from 'axios';
import { ID_REGISTER } from 'civil-status/components/shared/list-register';

interface IcaroDetailProps {
  isRejection?: boolean;
}

const IcaroDetailPage = ({ isRejection = false }: IcaroDetailProps): ReactElement => {
  const params = useParams();
  const navigate = useNavigate();

  const [isRejectionState, setIsRejectionState] = useState<boolean>(false);

  useEffect(() => {
    if (isRejection) {
      setIsRejectionState(isRejection);
    }
  }, [isRejection]);

  const [declarationData, getDeclarationData] = useHttpFetch<
    HttpResponse<DataDeclarationIcaro>,
    FormDeclarationIcaroVM
  >('', {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformerDeclarationIcaro,
  });

  const declarationDataCasted = (declarationData.data || {}) as FormDeclarationIcaroVM;

  useEffect(() => {
    getDeclarationData({
      url: `${__ACT_API_URL__}/ricerca-dichiarazione-nascita/dichiarazione/${params.contatore}`,
    });
  }, [params.contatore]);

  const itemsBreadcrubs: Array<BreadcrumbItem> = useMemo(() => {
    return IcaroDetailCrumbs(params.valueCat || '', params.idCat || '', params.contatore);
  }, [params.valueCat, params.idCat, params.contatore]);

  const title: string = useMemo(() => {
    switch (params.idCat) {
      case ID_REGISTER.NASCITA:
        return 'Dichiarazioni di nascita da icaro';
      default:
        return 'Icaro';
    }
  }, [params.idCat]);

  const subTitle: string = useMemo(() => {
    return `Scenario della dichiarazione inviata da ${
      declarationDataCasted.dataDeclaration?.hospital?.toUpperCase() || ''
    } Prot. num. ${declarationDataCasted.dataDeclaration?.protocolHospital || ''} del ${
      declarationDataCasted.dataDeclaration?.dateDeclaration
        ? declarationDataCasted.dataDeclaration?.dateDeclaration.toFormat('dd/MM/yyyy')
        : ''
    } ${declarationDataCasted.dataDeclaration?.timeDeclaration ? 'alle ore' : ''} ${
      declarationDataCasted.dataDeclaration?.timeDeclaration
        ? declarationDataCasted.dataDeclaration?.timeDeclaration.toFormat('HH:mm')
        : ''
    }`;
  }, [declarationDataCasted]);

  const goBack = useEventCallback(() => {
    navigate({
      pathname: generatePath('/stato-civile/:valueCat/:idCat/ricerca-icaro', {
        valueCat: params.valueCat || '',
        idCat: params.idCat || '',
      }),
    });
  });

  const onDrafting = useHttpClient(
    `${__ACT_API_URL__}/ricerca-dichiarazione-nascita/dichiarazione/stesura/${params.contatore}`,
    {
      options: {
        method: 'POST',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const onClickInsertEvent = useEventCallback(() => {
    (onDrafting() as ResponsePromise<void>).then((response) => {
      const { idEvento } = (
        response as unknown as AxiosResponse<HttpResponse<{ idEvento?: number }>>
      ).data.payload;

      toast.success('Operazione eseguita con successo');

      if (!idEvento) {
        return;
      }

      navigate({
        pathname: generatePath('/stato-civile/ricerca-atto-da-compilare/modifica/:idEvento', {
          idEvento: idEvento?.toString(),
        }),
      });
    });
  });

  const onClickCancel = useEventCallback(() => {
    setIsRejectionState(true);
  });

  const onRejection = useHttpClient(
    `${__ACT_API_URL__}/ricerca-dichiarazione-nascita/dichiarazione/rifiuto`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const onSubmit = useEventCallback((values: FormDeclarationIcaroVM) => {
    const data = {
      key: {
        contatore: params.contatore ? Number.parseInt(params.contatore, 10) : undefined,
      },
      motivoRifiuto: values.dataDeclaration?.reasonRefusal || undefined,
    };

    (
      onRejection({
        data,
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');

      goBack();
    });
  });

  const isLoading = declarationData.isLoading || declarationData.isLoading;

  return (
    <Fragment>
      <Breadcrumbs items={itemsBreadcrubs} />
      <div
        className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between"
        style={{ display: 'block' }}
      >
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          {title}
        </Typography>
        <Typography
          color="secondary.main"
          style={{
            fontSize: '20px',
          }}
        >
          {subTitle}
        </Typography>
      </div>
      {isLoading ? undefined : (
        <Formik
          initialValues={declarationDataCasted}
          validationSchema={isRejectionState ? validationSchemaIcaro : validationSchemaIcaroEmpty}
          validateOnMount
          onSubmit={onSubmit}
          enableReinitialize
          isInitialValid
        >
          <Form>
            <IcaroAccordions
              goBack={goBack}
              onClickInsertEvent={onClickInsertEvent}
              onClickCancel={onClickCancel}
              isRejectionState={isRejectionState}
            />
          </Form>
        </Formik>
      )}
    </Fragment>
  );
};

IcaroDetailPage.displayName = 'Research Icaro Page component';

export default IcaroDetailPage;
